import { cva, type VariantProps } from "class-variance-authority";
import { Check, ChevronDown } from "lucide-react";
import {
  Children,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";
import {
  onListboxOptionPointerDown,
  onListboxOptionTouchEnd,
  onListboxOptionTouchMove,
  onListboxOptionTouchStart,
} from "@/lib/combobox-listbox";
import {
  computeProSelectListboxPosition,
  isProSelectListboxTarget,
  PRO_SELECT_LISTBOX_ATTR,
  type ProSelectListboxPosition,
  resolveProSelectPortalRoot,
} from "@/lib/pro-select-portal";
import { proControlClassName, proControlErrorClassName } from "./pro-input";
import { type ProSelectOption, useProSelectKeyboard } from "./pro-select-keyboard";

/** Above form fields inside drawers (z-50). */
const PRO_SELECT_LISTBOX_Z_INDEX = 60;

function createSelectChangeEvent(select: HTMLSelectElement): React.ChangeEvent<HTMLSelectElement> {
  const nativeEvent = new Event("change", { bubbles: true });
  return {
    target: select,
    currentTarget: select,
    bubbles: true,
    cancelable: false,
    defaultPrevented: false,
    eventPhase: 0,
    isTrusted: false,
    nativeEvent,
    preventDefault: () => undefined,
    isDefaultPrevented: () => false,
    stopPropagation: () => undefined,
    isPropagationStopped: () => false,
    persist: () => undefined,
    timeStamp: Date.now(),
    type: "change",
  };
}

function createSelectBlurEvent(
  select: HTMLSelectElement | null,
): React.FocusEvent<HTMLSelectElement> {
  const nativeEvent = new FocusEvent("blur", { bubbles: true });
  return {
    target: select as EventTarget & HTMLSelectElement,
    currentTarget: select as EventTarget & HTMLSelectElement,
    bubbles: true,
    cancelable: false,
    defaultPrevented: false,
    eventPhase: 0,
    isTrusted: false,
    nativeEvent,
    preventDefault: () => undefined,
    isDefaultPrevented: () => false,
    stopPropagation: () => undefined,
    isPropagationStopped: () => false,
    persist: () => undefined,
    timeStamp: Date.now(),
    type: "blur",
    relatedTarget: null,
  };
}

const proSelectTriggerVariants = cva(
  cn(
    "flex w-full items-center justify-between gap-2 text-left",
    proControlClassName,
    "disabled:opacity-100",
  ),
  {
    variants: {
      size: {
        default: "h-10 px-3.5 text-[16px]",
        sm: "h-10 px-3.5 text-[16px] md:h-8 md:px-2 md:text-[13px]",
      },
    },
    defaultVariants: { size: "default" },
  },
);

type ProSurfaceClass = "pro-dark" | "pro-light";

function resolveProSurfaceClassName(node: HTMLElement | null): ProSurfaceClass {
  const surface = node?.closest(".pro-light, .pro-dark");
  return surface?.classList.contains("pro-light") ? "pro-light" : "pro-dark";
}

/** Portaled menus need pro surface class — body text color is consumer --ink otherwise. */
const proSelectListboxClassName =
  "max-h-56 overflow-y-auto overscroll-y-contain rounded-[8px] border border-pro-border bg-pro-card py-1 text-pro-text shadow-pop";

function parseSelectOptions(children: React.ReactNode): ProSelectOption[] {
  const options: ProSelectOption[] = [];
  Children.forEach(children, (child) => {
    if (
      !isValidElement<{ value?: string | number; disabled?: boolean; children?: React.ReactNode }>(
        child,
      )
    ) {
      return;
    }
    if (child.type !== "option") return;
    const value = child.props.value === undefined ? "" : String(child.props.value);
    const label =
      typeof child.props.children === "string"
        ? child.props.children
        : value || String(child.props.children ?? "");
    options.push({ value, label, disabled: child.props.disabled });
  });
  return options;
}

function findSelectedOption(
  options: ProSelectOption[],
  value: string,
): ProSelectOption | undefined {
  return options.find((option) => option.value === value);
}

interface ProSelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "size" | "children">,
    VariantProps<typeof proSelectTriggerVariants> {
  error?: boolean;
  children: React.ReactNode;
  /** Shown on the trigger when no option is selected (value is empty). */
  placeholder?: string;
  /** Hide the chevron and center the label (e.g. compact HH:mm time fields). */
  hideChevron?: boolean;
  /** Minimum width for the portaled listbox (defaults to trigger width). */
  menuMinWidth?: number;
}

const ProSelect = forwardRef<HTMLSelectElement, ProSelectProps>(
  // fallow-ignore-next-line complexity
  (
    {
      children,
      className,
      error,
      size,
      disabled,
      id,
      name,
      value,
      defaultValue,
      onChange,
      onBlur,
      placeholder,
      hideChevron = false,
      menuMinWidth,
      "aria-label": ariaLabel,
      "aria-required": ariaRequired,
      "aria-describedby": ariaDescribedBy,
      "aria-labelledby": ariaLabelledBy,
      "aria-invalid": ariaInvalid,
      required,
      ...rest
    },
    ref,
  ) => {
    const listboxId = useId();
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const hiddenSelectRef = useRef<HTMLSelectElement>(null);
    const listboxRef = useRef<HTMLDivElement>(null);
    const portalRootRef = useRef<HTMLElement>(document.body);
    const [open, setOpen] = useState(false);
    const [panelStyle, setPanelStyle] = useState<ProSelectListboxPosition | null>(null);
    const [surfaceClass, setSurfaceClass] = useState<ProSurfaceClass>("pro-dark");

    const options = useMemo(() => parseSelectOptions(children), [children]);
    const isControlled = value !== undefined;
    const [uncontrolledValue, setUncontrolledValue] = useState(() =>
      defaultValue === undefined ? "" : String(defaultValue),
    );
    const currentValue = isControlled ? String(value ?? "") : uncontrolledValue;

    useLayoutEffect(() => {
      if (isControlled) return;
      const select = hiddenSelectRef.current;
      if (!select) return;
      setUncontrolledValue((prev) => (prev === select.value ? prev : select.value));
    });

    const selectedOption = findSelectedOption(options, currentValue);
    const displayLabel =
      selectedOption?.label ??
      (currentValue === ""
        ? (placeholder ?? options.find((o) => o.value === "")?.label)
        : currentValue);

    const updatePanelPosition = useCallback(() => {
      const trigger = triggerRef.current;
      if (!trigger) return;

      const portalRoot = resolveProSelectPortalRoot(containerRef.current);
      portalRootRef.current = portalRoot;
      setSurfaceClass(resolveProSurfaceClassName(containerRef.current));
      setPanelStyle(computeProSelectListboxPosition(trigger, portalRoot, menuMinWidth));
    }, [menuMinWidth]);

    useLayoutEffect(() => {
      if (!open) return;
      updatePanelPosition();
      const onReposition = () => updatePanelPosition();
      window.addEventListener("resize", onReposition);
      window.addEventListener("scroll", onReposition, true);
      return () => {
        window.removeEventListener("resize", onReposition);
        window.removeEventListener("scroll", onReposition, true);
      };
    }, [open, updatePanelPosition]);

    useEffect(() => {
      if (!open) return;
      const onDocumentPointerDown = (event: PointerEvent) => {
        const target = event.target as Node;
        if (containerRef.current?.contains(target)) return;
        if (listboxRef.current?.contains(target)) return;
        if (isProSelectListboxTarget(event.target)) return;
        setOpen(false);
      };
      document.addEventListener("pointerdown", onDocumentPointerDown);
      return () => document.removeEventListener("pointerdown", onDocumentPointerDown);
    }, [open]);

    const emitChange = (select: HTMLSelectElement) => {
      onChange?.(createSelectChangeEvent(select));
    };

    const commitValue = (nextValue: string) => {
      const select = hiddenSelectRef.current;
      if (!select) return;

      select.value = nextValue;
      if (!isControlled) {
        setUncontrolledValue(nextValue);
      }
      emitChange(select);
    };

    const selectOption = (option: ProSelectOption) => {
      if (option.disabled) return;
      commitValue(option.value);
      setOpen(false);
      triggerRef.current?.focus({ preventScroll: true });
    };

    const keyboard = useProSelectKeyboard({
      options,
      value: currentValue,
      open,
      onOpen: () => {
        updatePanelPosition();
        setOpen(true);
      },
      onClose: () => setOpen(false),
      onSelect: selectOption,
    });
    const activeOptionId =
      open && !disabled && !options[keyboard.activeIndex]?.disabled && options[keyboard.activeIndex]
        ? `${listboxId}-${keyboard.activeIndex}`
        : undefined;

    useEffect(() => {
      if (activeOptionId)
        document.getElementById(activeOptionId)?.scrollIntoView?.({ block: "nearest" });
    }, [activeOptionId]);

    const chevronClassName = cn(
      "shrink-0 text-pro-text-faint transition-transform duration-150",
      size === "sm" ? "size-3.5" : "size-4",
      open && "rotate-180",
    );

    const listbox =
      open && !disabled && panelStyle
        ? createPortal(
            <div
              ref={listboxRef}
              id={listboxId}
              role="listbox"
              aria-label={ariaLabel}
              aria-labelledby={ariaLabelledBy ?? (id ? `${id}-value` : undefined)}
              {...{ [PRO_SELECT_LISTBOX_ATTR]: "" }}
              className={cn(
                proSelectListboxClassName,
                surfaceClass,
                "pointer-events-auto touch-manipulation",
              )}
              style={{
                position: panelStyle.position,
                top: panelStyle.top,
                left: panelStyle.left,
                width: panelStyle.width,
                maxHeight: panelStyle.maxHeight,
                zIndex: PRO_SELECT_LISTBOX_Z_INDEX,
              }}
            >
              {options.map((option, index) => {
                const isSelected = option.value === currentValue;
                return (
                  <button
                    key={`${option.value}-${option.label}`}
                    type="button"
                    role="option"
                    id={`${listboxId}-${index}`}
                    aria-selected={isSelected}
                    tabIndex={-1}
                    disabled={option.disabled}
                    className={cn(
                      "flex w-full items-center text-pro-text transition-colors",
                      hideChevron
                        ? "justify-center px-2 py-2 text-center text-[13px] tabular-nums"
                        : cn(
                            "justify-between gap-2 px-3.5 py-2.5 text-left text-[14px]",
                            size === "sm" && "px-2 py-2 text-[13px]",
                          ),
                      !option.disabled && !isSelected && "hover:bg-pro-surface",
                      isSelected &&
                        cn(
                          "bg-lime-subtle/40",
                          surfaceClass === "pro-light" ? "text-lime-text" : "text-lime",
                        ),
                      option.disabled && "cursor-not-allowed text-pro-text-faint opacity-55",
                      index === keyboard.activeIndex && "outline-2 -outline-offset-2 outline-lime",
                    )}
                    onClick={(event) => {
                      if (event.detail === 0) selectOption(option);
                    }}
                    onTouchStart={onListboxOptionTouchStart}
                    onTouchMove={onListboxOptionTouchMove}
                    onTouchEnd={(event) => {
                      onListboxOptionTouchEnd(event, () => selectOption(option), {
                        ghostClickRoot: portalRootRef.current,
                      });
                    }}
                    onPointerDown={(event) => {
                      if (event.pointerType === "touch") return;
                      onListboxOptionPointerDown(event, () => selectOption(option));
                    }}
                  >
                    <span className={cn(hideChevron ? "shrink-0" : "min-w-0 truncate")}>
                      {option.label}
                    </span>
                    {isSelected && !hideChevron && (
                      <Check className="size-4 shrink-0" aria-hidden strokeWidth={2.5} />
                    )}
                  </button>
                );
              })}
            </div>,
            portalRootRef.current,
          )
        : null;

    return (
      <div
        ref={containerRef}
        className={cn("relative w-full max-w-full", disabled && "opacity-50", className)}
      >
        <select
          ref={(node) => {
            hiddenSelectRef.current = node;
            if (typeof ref === "function") {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          name={name}
          disabled={disabled}
          className="pointer-events-none absolute h-px w-px overflow-hidden border-0 p-0 opacity-0"
          tabIndex={-1}
          aria-hidden
          {...(isControlled ? { value: currentValue } : { defaultValue })}
          onChange={(event) => {
            if (!isControlled) {
              setUncontrolledValue(event.target.value);
            }
            onChange?.(event);
          }}
          onBlur={onBlur}
          aria-required={ariaRequired}
          required={required}
          {...rest}
        >
          {children}
        </select>

        <button
          ref={triggerRef}
          id={id}
          type="button"
          role="combobox"
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid ?? (error || undefined)}
          aria-required={ariaRequired ?? required}
          aria-expanded={open && !disabled}
          aria-controls={open && !disabled ? listboxId : undefined}
          aria-activedescendant={activeOptionId}
          aria-haspopup="listbox"
          disabled={disabled}
          className={cn(
            proSelectTriggerVariants({ size }),
            error && proControlErrorClassName,
            open && "border-lime shadow-[0_0_0_2px_var(--lime-ring)]",
            !selectedOption && currentValue === "" && "text-pro-text-faint",
            disabled && "cursor-not-allowed",
            hideChevron && "justify-center gap-0 px-1.5 text-center text-[15px] md:text-[13px]",
          )}
          onClick={(event) => {
            if (disabled) return;
            event.currentTarget.focus({ preventScroll: true });
            if (open) setOpen(false);
            else keyboard.openOptions();
          }}
          onKeyDown={keyboard.onKeyDown}
          onBlur={(event) => {
            if (listboxRef.current?.contains(event.relatedTarget)) return;
            // Touch selection can blur the trigger without a new focus target
            // before touchend; outside pointers and Tab dismiss independently.
            if (event.relatedTarget) setOpen(false);
            onBlur?.(createSelectBlurEvent(hiddenSelectRef.current));
          }}
        >
          <span
            id={id ? `${id}-value` : undefined}
            className={cn(hideChevron ? "w-full text-center" : "min-w-0 flex-1 truncate")}
          >
            {displayLabel}
          </span>
          {!hideChevron && <ChevronDown aria-hidden className={chevronClassName} strokeWidth={2} />}
        </button>

        {listbox}
      </div>
    );
  },
);

ProSelect.displayName = "ProSelect";

export {
  ProSelect,
  type ProSelectOption,
  type ProSelectProps,
  parseSelectOptions,
  proSelectListboxClassName,
  proSelectTriggerVariants,
};
